import React from 'react';
import { ForgeCircularProgress } from '@tylertech/forge-react';
import { Nullable } from 'common/types/nullable';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';
import { AsyncValueResult } from 'common/providers/AsyncValues/types';

const translate = getScopedTranslateMethod('common.providers');

/** Props for AsyncValueDisplay component. */
interface AsyncValueDisplayProps<T> {
  result: AsyncValueResult<T>;
  renderValue: (value: T) => React.ReactNode;
  renderError?: (error: string) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  renderEmpty?: () => React.ReactNode;
  loadingAriaLabel?: string;
  loadingSize?: string;
}

/** Generic component for displaying async values with loading, error, and success states. */
const AsyncValueDisplay = <T,>({
  result,
  renderValue,
  renderError,
  renderLoading,
  renderEmpty,
  loadingAriaLabel = 'Loading',
  loadingSize = '20px'
}: AsyncValueDisplayProps<T>): Nullable<React.ReactElement> => {
  const { value, loading, error } = result;

  let content: React.ReactNode;
  if (error) content = renderError?.(error) ?? translate('errors.generic_error', { error });
  else if (loading)
    content = renderLoading?.() ?? (
      <ForgeCircularProgress
        aria-label={loadingAriaLabel}
        style={{ '--forge-circular-progress-size': loadingSize, display: 'inline-block' }}
      />
    );
  else if (value !== null) content = renderValue(value);
  else content = renderEmpty?.() ?? null;

  return <>{content}</>;
};

export default AsyncValueDisplay;
